import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  brandingFooterLine,
  brandingPoweredByLine,
  loadBrandingLogoDataUrl,
  pdfImageFormat,
  resolveExportBranding,
} from './tenantBranding';

const BRAND = {
  subtitle: 'Professional Business Report',
  primary: [15, 23, 42],
  accent: [99, 102, 241],
  muted: [100, 116, 139],
  border: [203, 213, 225],
};

/** Typography + colour presets for PDF table exports. */
const PDF_THEMES = {
  default: {
    accent: [99, 102, 241],
    accentDark: [79, 70, 229],
    headerBg: [248, 250, 252],
    tableHead: [99, 102, 241],
    tableHeadText: [255, 255, 255],
    tableBody: [30, 41, 59],
    tableAlt: [249, 250, 251],
    titleFont: 'helvetica',
    bodyFont: 'times',
    titleSize: 18,
    subtitleSize: 10,
    tableHeadSize: 10,
    tableBodySize: 9.5,
    cellPadding: 6,
  },
  'factory-production': {
    accent: [5, 150, 105],
    accentDark: [4, 120, 87],
    headerBg: [236, 253, 245],
    tableHead: [5, 120, 84],
    tableHeadText: [255, 255, 255],
    tableBody: [15, 23, 42],
    tableAlt: [240, 253, 250],
    titleFont: 'helvetica',
    bodyFont: 'times',
    titleSize: 19,
    subtitleSize: 10.5,
    tableHeadSize: 10,
    tableBodySize: 9.5,
    cellPadding: 6,
  },
  'factory-damage': {
    accent: [185, 28, 28],
    accentDark: [153, 27, 27],
    headerBg: [255, 241, 242],
    tableHead: [185, 28, 28],
    tableHeadText: [255, 255, 255],
    tableBody: [30, 27, 27],
    tableAlt: [254, 242, 242],
    titleFont: 'helvetica',
    bodyFont: 'times',
    titleSize: 19,
    subtitleSize: 10.5,
    tableHeadSize: 10,
    tableBodySize: 9.5,
    cellPadding: 6,
  },
};

function resolvePdfTheme(options) {
  const key = options?.theme;
  return PDF_THEMES[key] || PDF_THEMES.default;
}

function drawAccentRule(doc, x, y, width, theme) {
  doc.saveGraphicsState();
  doc.setFillColor(...theme.accent);
  doc.roundedRect(x, y, width, 3, 1.5, 1.5, 'F');
  doc.setFillColor(...theme.accentDark);
  doc.roundedRect(x + width * 0.62, y, width * 0.38, 3, 1.5, 1.5, 'F');
  doc.restoreGraphicsState();
}

function drawPageBorder(doc, pageWidth, pageHeight) {
  const inset = 14;
  doc.saveGraphicsState();
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.85);
  doc.roundedRect(inset, inset, pageWidth - 2 * inset, pageHeight - 2 * inset, 7, 7, 'S');
  doc.restoreGraphicsState();
}

function formatPoDisplayDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(value);
  }
}

function resolveOrderApprovedBy(order) {
  const approver = order?.approver;
  if (approver?.name || approver?.email) {
    return approver.name || approver.email;
  }
  const logs = order?.approval_logs || order?.approvalLogs || [];
  const review = logs.find((log) => log.event === 'approved' || log.event === 'rejected');
  const actor = review?.actor;
  if (actor?.name || actor?.email) {
    return actor.name || actor.email;
  }
  return '—';
}

/** Supplier-facing line remark — hide internal stock/workflow notes from PO PDF. */
function supplierFacingPoRemark(order, lineReason) {
  const raw = String(lineReason || '').trim();
  const isInternal =
    /on hand:/i.test(raw) ||
    /^material order/i.test(raw) ||
    /^low stock/i.test(raw) ||
    /^factory material/i.test(raw);
  if (isInternal || !raw) {
    return order?.payment_terms?.trim() || 'Per purchase order';
  }
  return raw;
}

function poStatusRibbonConfig(status) {
  const raw = String(status || 'pending_approval').toLowerCase();
  const map = {
    approved: { label: 'APPROVED', color: [22, 163, 74] },
    rejected: { label: 'REJECTED', color: [220, 38, 38] },
    pending: { label: 'PENDING', color: [217, 119, 6] },
    pending_approval: { label: 'PENDING', color: [217, 119, 6] },
    ordered: { label: 'ORDERED', color: [37, 99, 235] },
    partially_received: { label: 'PARTIAL', color: [13, 148, 136] },
    received: { label: 'RECEIVED', color: [5, 150, 105] },
    cancelled: { label: 'CANCELLED', color: [100, 116, 139] },
  };
  return (
    map[raw] || {
      label: raw.replace(/_/g, ' ').toUpperCase(),
      color: [100, 116, 139],
    }
  );
}

/** Top-right corner triangle stamp — covers the full corner (PAID-invoice style). */
function drawPoStatusRibbon(doc, pageWidth, status) {
  const cfg = poStatusRibbonConfig(status);
  const inset = 14;
  const [r, g, b] = cfg.color;
  const right = pageWidth - inset;
  const top = inset;
  const label = cfg.label;
  const angle = 45;

  const cornerSize = label.length > 9 ? 128 : 118;
  const triRight = right;
  const triTop = top;
  const triBottom = top + cornerSize;
  const triLeft = right - cornerSize;

  doc.setFillColor(Math.max(0, r - 35), Math.max(0, g - 35), Math.max(0, b - 35));
  doc.triangle(triRight + 2, triTop + 2, triRight + 2, triBottom + 2, triLeft + 2, triTop + 2, 'F');

  doc.setFillColor(r, g, b);
  doc.setDrawColor(r, g, b);
  doc.triangle(triRight, triTop, triRight, triBottom, triLeft, triTop, 'FD');

  let fontSize = 13;
  if (label.length > 9) fontSize = 10;
  else if (label.length > 7) fontSize = 11;

  const rcx = right - cornerSize * 0.36;
  const rcy = top + cornerSize * 0.36;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(fontSize);
  doc.setTextColor(255, 255, 255);
  doc.text(label, rcx, rcy, {
    align: 'center',
    baseline: 'middle',
    angle,
    renderingMode: 'fill',
  });
}

function escapeCsvRow(row) {
  return row
    .map((value) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    })
    .join(',');
}

export function downloadCsv(filename, rows, options = {}) {
  const branding = resolveExportBranding(options);
  const base = safeFilename(branding.businessName);
  const file = filename.includes(base) ? filename : `${base}_${filename}`;
  const generatedAt = new Date().toLocaleString('en-GB', { hour12: false });
  const preamble = [];
  if (options.title || options.subtitle) {
    preamble.push([branding.businessName || 'StockTrack']);
    if (options.title) preamble.push([options.title]);
    if (options.subtitle) preamble.push([options.subtitle]);
    preamble.push([`Generated: ${generatedAt}`]);
    preamble.push([]);
  }
  const allRows = [...preamble, ...rows];
  const csv = `\uFEFF${allRows.map(escapeCsvRow).join('\n')}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function addLogoImage(doc, logoDataUrl, x, y, size) {
  if (!logoDataUrl) return;
  const fmt = pdfImageFormat(logoDataUrl);
  doc.addImage(logoDataUrl, fmt, x, y, size, size);
}

function safeFilename(title, branding) {
  return String(title)
    .replace(/[^\w-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'report';
}

function buildNumericColumnStyles(headers) {
  const numericHints = ['amount', 'total', 'balance', 'qty', 'quantity', 'rate', 'commission', 'cost', 'profit', 'pages', 'value'];
  const out = {};
  headers.forEach((h, idx) => {
    const key = String(h || '').toLowerCase();
    if (numericHints.some((hint) => key.includes(hint))) {
      out[idx] = { halign: 'right' };
    }
  });
  return out;
}

function drawCriteriaSection(doc, margin, startY, pageWidth, criteria = [], summary = []) {
  let y = startY;
  const contentWidth = pageWidth - margin * 2;
  const safeCriteria = Array.isArray(criteria) ? criteria.filter(Boolean).slice(0, 8) : [];
  const safeSummary = Array.isArray(summary) ? summary.filter((s) => s?.label).slice(0, 4) : [];

  if (safeCriteria.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.primary);
    doc.text('Report criteria', margin, y);
    y += 13;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    for (const line of safeCriteria) {
      const wrapped = doc.splitTextToSize(`• ${line}`, contentWidth);
      doc.text(wrapped, margin, y);
      y += wrapped.length * 11;
    }
    y += 4;
  }

  if (safeSummary.length > 0) {
    const cardGap = 8;
    const cardWidth = (contentWidth - cardGap) / 2;
    const cardHeight = 36;
    safeSummary.forEach((item, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const x = margin + col * (cardWidth + cardGap);
      const cardY = y + row * (cardHeight + 6);
      doc.saveGraphicsState();
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(...BRAND.border);
      doc.roundedRect(x, cardY, cardWidth, cardHeight, 5, 5, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...BRAND.muted);
      doc.text(String(item.label), x + 8, cardY + 12);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.primary);
      doc.text(String(item.value ?? '—'), x + 8, cardY + 26);
      doc.restoreGraphicsState();
    });
    y += Math.ceil(safeSummary.length / 2) * (cardHeight + 6) + 6;
  }
  return y;
}

/** Shared compact headers for purchase-order list PDFs (all business types). */
export const ORDERS_LIST_PDF_HEADERS = ['PO #', 'Date', 'Supplier', 'Status', 'Product', 'Qty', 'Amount'];

const ORDERS_LIST_PDF_COLUMN_STYLES = {
  0: { cellWidth: 50 },
  1: { cellWidth: 58 },
  2: { cellWidth: 88 },
  3: { cellWidth: 68 },
  4: { cellWidth: 152 },
  5: { cellWidth: 34, halign: 'right' },
  6: { cellWidth: 72, halign: 'right' },
};

const ORDER_LINES_PDF_HEADERS = ['Product', 'Qty', 'Unit cost', 'Amount'];

const ORDER_LINES_PDF_COLUMN_STYLES = {
  0: { cellWidth: 'auto' },
  1: { cellWidth: 42, halign: 'right' },
  2: { cellWidth: 78, halign: 'right' },
  3: { cellWidth: 82, halign: 'right' },
};

function formatExportPdfDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function formatExportPdfStatus(status) {
  return String(status || 'pending_approval')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatExportPdfMoney(value) {
  const amount = Math.round(Number(value) || 0);
  return amount > 0 ? `TZS ${amount.toLocaleString()}` : '—';
}

/** Compact rows for purchase-order list PDFs — used by every business type. */
export function buildOrdersListPdfRows(orders) {
  const rows = [];
  for (const order of orders || []) {
    const products = order.product_requests || order.productRequests || [];
    const supplierName = order.supplier_name || order.supplier?.name || '—';
    const st = formatExportPdfStatus(order.status);
    const poNumber = order.po_number || `PO-${order.id}`;
    const date = formatExportPdfDate(order.created_at);

    if (products.length === 0) {
      rows.push([
        poNumber,
        date,
        supplierName,
        st,
        '—',
        '—',
        formatExportPdfMoney(order.estimated_total_tzs),
      ]);
      continue;
    }

    for (const p of products) {
      const qty = Number(p.quantity);
      const lineTotal =
        p.estimated_line_total_tzs != null
          ? p.estimated_line_total_tzs
          : (Number.isFinite(qty) ? qty : 0) * (Number(p.estimated_unit_cost_tzs) || 0);
      rows.push([
        poNumber,
        date,
        supplierName,
        st,
        p.product_name || '—',
        Number.isFinite(qty) && qty > 0 ? String(qty) : '—',
        formatExportPdfMoney(lineTotal),
      ]);
    }
  }
  return rows;
}

/**
 * Export purchase-order list PDF — same compact layout for shop, pharmacy, bar, factory, automotive, etc.
 */
export async function exportOrdersListPdf(orders, options = {}) {
  const {
    filterLabel = 'All',
    filter = 'all',
    title = 'Purchase orders',
    branding,
  } = options;
  const rows = buildOrdersListPdfRows(orders);
  const orderCount = (orders || []).length;
  const totalTzs = (orders || []).reduce((sum, o) => sum + (Number(o.estimated_total_tzs) || 0), 0);

  await exportTableToPdf(`${title} – ${filterLabel}`, ORDERS_LIST_PDF_HEADERS, rows, {
    branding,
    criteria: filter !== 'all' ? [{ label: 'Filter', value: filterLabel }] : [],
    summary: [
      { label: 'Orders', value: orderCount },
      { label: 'Line items', value: rows.length },
      { label: 'Est. total', value: `TZS ${Math.round(totalTzs).toLocaleString()}` },
    ],
    columnStyles: ORDERS_LIST_PDF_COLUMN_STYLES,
    tableStyles: { overflow: 'linebreak' },
  });
}

function buildOrderLinePdfRows(products, useActuals = false) {
  return (products || []).map((p) => {
    const qty = useActuals ? Number(p.received_quantity || 0) : Number(p.quantity || 0);
    const unit = useActuals
      ? Number(p.actual_unit_cost_tzs ?? p.estimated_unit_cost_tzs ?? 0)
      : Number(p.estimated_unit_cost_tzs || 0);
    const line = useActuals
      ? Number(p.received_line_total_tzs ?? qty * unit)
      : Number(p.estimated_line_total_tzs ?? qty * unit);
    return [
      p.product_name || '—',
      String(Math.max(0, qty)),
      formatExportPdfMoney(unit),
      formatExportPdfMoney(line),
    ];
  });
}

export async function exportTableToPdf(title, headers, rows, options = {}) {
  const branding = resolveExportBranding(options);
  const theme = resolvePdfTheme(options);
  const generatedAt = new Date().toLocaleString('en-GB', { hour12: false });
  const subtitle = options?.subtitle || BRAND.subtitle;
  const criteria = options?.criteria || [];
  const summary = options?.summary || [];
  const poweredBy = brandingPoweredByLine(branding);

  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const footerHeight = 92;
  const centerX = pageWidth / 2;
  const contentWidth = pageWidth - margin * 2;
  const numericColumnStyles = buildNumericColumnStyles(headers);
  const columnStyles = { ...numericColumnStyles, ...(options.columnStyles || {}) };
  const body = (rows || []).map((r) => r.map((c) => (c == null ? '' : String(c))));

  const logoDataUrl = await loadBrandingLogoDataUrl(branding);

  let cursorY = margin;
  const headerHeight = 132;

  doc.saveGraphicsState();
  doc.setFillColor(...theme.headerBg);
  doc.roundedRect(margin - 6, cursorY - 8, contentWidth + 12, headerHeight, 10, 10, 'F');
  doc.restoreGraphicsState();

  let textStartY = cursorY + 16;
  if (logoDataUrl) {
    const logoSize = 44;
    addLogoImage(doc, logoDataUrl, centerX - logoSize / 2, cursorY + 4, logoSize);
    textStartY = cursorY + 54;
  }

  doc.setFont(theme.titleFont, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...theme.accentDark);
  doc.text(String(branding.businessName).toUpperCase(), centerX, textStartY, { align: 'center' });
  if (branding.phone) {
    textStartY += 11;
    doc.setFont(theme.bodyFont, 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...BRAND.muted);
    doc.text(branding.phone, centerX, textStartY, { align: 'center' });
    textStartY += 2;
  }

  drawAccentRule(doc, centerX - 72, textStartY + 14, 144, theme);

  doc.setFont(theme.titleFont, 'bold');
  doc.setFontSize(theme.titleSize);
  doc.setTextColor(...BRAND.primary);
  doc.text(title, centerX, textStartY + 34, { align: 'center' });

  doc.setFont(theme.bodyFont, 'italic');
  doc.setFontSize(theme.subtitleSize);
  doc.setTextColor(...BRAND.muted);
  doc.text(subtitle, centerX, textStartY + 52, { align: 'center' });

  doc.setFont(theme.bodyFont, 'normal');
  doc.setFontSize(8.5);
  doc.text(`Generated: ${generatedAt}`, centerX, textStartY + 66, { align: 'center' });
  cursorY += 134;

  cursorY = drawCriteriaSection(doc, margin, cursorY, pageWidth, criteria, summary);
  cursorY += 10;

  autoTable(doc, {
    startY: cursorY,
    head: [headers],
    body,
    theme: 'grid',
    styles: {
      font: theme.bodyFont,
      fontSize: theme.tableBodySize,
      cellPadding: theme.cellPadding,
      lineColor: BRAND.border,
      lineWidth: 0.45,
      valign: 'middle',
      textColor: theme.tableBody,
      ...(options.tableStyles || {}),
    },
    headStyles: {
      font: theme.titleFont,
      fontStyle: 'bold',
      fontSize: theme.tableHeadSize,
      fillColor: theme.tableHead,
      textColor: theme.tableHeadText,
      cellPadding: theme.cellPadding + 1,
    },
    bodyStyles: { fillColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: theme.tableAlt },
    tableLineColor: [148, 163, 184],
    tableLineWidth: 0.6,
    margin: { left: margin, right: margin, top: cursorY, bottom: margin + footerHeight },
    columnStyles: columnStyles,
    didParseCell: (ctx) => {
      if (ctx.section === 'body') {
        const row = ctx.row.raw || [];
        const firstCell = String(row[0] || '').toUpperCase();
        if (firstCell.startsWith('TOTAL')) {
          ctx.cell.styles.fontStyle = 'bold';
          ctx.cell.styles.font = theme.titleFont;
          ctx.cell.styles.fillColor = theme.tableAlt;
        }
      }
    },
    didDrawPage: (data) => {
      const page = data.pageNumber;
      const total = doc.internal.getNumberOfPages();
      const bottomY = pageHeight - margin;

      if (page > 1) {
        doc.setFont(theme.titleFont, 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...BRAND.primary);
        doc.text(title, centerX, margin, { align: 'center' });
        doc.setFont(theme.bodyFont, 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...BRAND.muted);
        doc.text(`Generated: ${generatedAt}`, centerX, margin + 14, { align: 'center' });
      }

      doc.setFont(theme.bodyFont, 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...BRAND.muted);
      doc.text(`Page ${page} of ${total}`, centerX, bottomY - 22, { align: 'center' });
      doc.setFont(theme.titleFont, 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.primary);
      doc.text(brandingFooterLine(branding), centerX, bottomY - 36, { align: 'center' });
      if (poweredBy) {
        doc.setFont(theme.bodyFont, 'italic');
        doc.setFontSize(7.5);
        doc.setTextColor(...BRAND.muted);
        doc.text(poweredBy, centerX, bottomY - 48, { align: 'center' });
      }

      drawPageBorder(doc, pageWidth, pageHeight);
    },
  });

  const filename = `${safeFilename(branding.businessName)}_${safeFilename(title)}.pdf`;
  doc.save(filename);
}

export async function exportPurchaseOrderPdf(order, options = {}) {
  if (!order) return;
  const branding = resolveExportBranding(options);
  const poweredBy = brandingPoweredByLine(branding);

  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const centerX = pageWidth / 2;
  const generatedAt = new Date().toLocaleString('sv-SE', { hour12: false });
  const products = order.product_requests || order.productRequests || [];
  const logoDataUrl = await loadBrandingLogoDataUrl(branding);

  drawPageBorder(doc, pageWidth, pageHeight);

  let y = margin;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin - 6, y - 8, pageWidth - margin * 2 + 12, 112, 8, 8, 'F');
  if (logoDataUrl) {
    addLogoImage(doc, logoDataUrl, centerX - 20, y + 4, 40);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.accent);
  doc.text(String(branding.businessName).toUpperCase(), centerX, y + 56, { align: 'center' });
  doc.setFontSize(17);
  doc.setTextColor(...BRAND.primary);
  doc.text(`Purchase Order ${order.po_number || `#${order.id}`}`, centerX, y + 78, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(`Generated: ${generatedAt}`, centerX, y + 94, { align: 'center' });

  // Draw status tag on top of header (APPROVED / REJECTED / etc.)
  drawPoStatusRibbon(doc, pageWidth, order.status);

  y += 128;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.primary);
  doc.text('PO Details', margin, y);
  y += 14;

  const submittedBy = order.user?.name || order.user?.email || '—';
  const approvedBy = resolveOrderApprovedBy(order);
  const statusLabel = String(order.status || 'pending_approval').replace(/_/g, ' ');
  const details = [
    ['PO Number', order.po_number || `#${order.id}`],
    ['Status', statusLabel],
    ['Requested by', submittedBy],
    ['Approved by', approvedBy],
    ['Approved on', formatPoDisplayDate(order.approved_at)],
    ['Supplier', order.supplier_name || order.supplier?.name || '—'],
    ['Supplier phone', order.supplier_phone || order.supplier?.phone || '—'],
    ['Needed by', formatPoDisplayDate(order.needed_by_date)],
    ['Payment terms', order.payment_terms || '—'],
    ['Payment due date', formatPoDisplayDate(order.payment_due_date)],
    [
      'Estimated total',
      `TZS ${Math.round(Number(order.estimated_total_tzs) || 0).toLocaleString()}`,
    ],
  ];

  autoTable(doc, {
    startY: y,
    body: details,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 5, lineColor: [203, 213, 225], lineWidth: 0.5 },
    columnStyles: {
      0: { cellWidth: 120, fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [15, 23, 42] },
      1: { cellWidth: pageWidth - margin * 2 - 120 },
    },
    margin: { left: margin, right: margin },
  });

  const rows = buildOrderLinePdfRows(products, false);

  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || y) + 18,
    head: [ORDER_LINES_PDF_HEADERS],
    body: rows,
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 5, lineColor: [203, 213, 225], lineWidth: 0.5, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: ORDER_LINES_PDF_COLUMN_STYLES,
    margin: { left: margin, right: margin, bottom: 70 },
  });

  const footerY = pageHeight - 42;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text('Supplier copy — authorized purchase order document.', centerX, footerY, { align: 'center' });
  if (poweredBy) {
    doc.text(poweredBy, centerX, footerY + 11, { align: 'center' });
  }

  doc.save(`${safeFilename(branding.businessName)}_${safeFilename(order.po_number || `po_${order.id}`)}.pdf`);
}

function orderBaseDocRows(order, useActuals = false) {
  const products = order?.product_requests || order?.productRequests || [];
  return products.map((p) => {
    const qty = useActuals ? Number(p.received_quantity || 0) : Number(p.quantity || 0);
    const unit = useActuals
      ? Number(p.actual_unit_cost_tzs ?? p.estimated_unit_cost_tzs ?? 0)
      : Number(p.estimated_unit_cost_tzs || 0);
    const line = useActuals
      ? Number(p.received_line_total_tzs ?? qty * unit)
      : Number(p.estimated_line_total_tzs ?? qty * unit);
    return {
      product: p.product_name || '',
      category: p.category || '',
      qty: Math.max(0, qty),
      unitCost: Math.max(0, unit),
      lineTotal: Math.max(0, line),
      note: p.reason || '',
    };
  });
}

function drawBusinessDocHeader(doc, order, docType, generatedAt, branding) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;
  const margin = 40;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin - 6, margin - 8, pageWidth - margin * 2 + 12, 122, 8, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.accent);
  doc.text(String(branding.businessName).toUpperCase(), centerX, margin + 20, { align: 'center' });
  doc.setFontSize(17);
  doc.setTextColor(...BRAND.primary);
  doc.text(docType, centerX, margin + 44, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(`Generated: ${generatedAt}`, centerX, margin + 60, { align: 'center' });
  doc.text(`PO Ref: ${order.po_number || `PO-${order.id}`}`, centerX, margin + 74, { align: 'center' });
}

function docMetaRows(order, docNumber, totalsLabel, totalsValue) {
  return [
    ['Document no.', docNumber],
    ['PO reference', order.po_number || `PO-${order.id}`],
    ['Supplier', order.supplier_name || order.supplier?.name || '—'],
    ['Supplier phone', order.supplier_phone || order.supplier?.phone || '—'],
    ['Requested by', order.user?.name || order.user?.email || '—'],
    ['Status', String(order.status || '').replace(/_/g, ' ') || 'pending'],
    ['Needed by', order.needed_by_date || '—'],
    ['Payment terms', order.payment_terms || '—'],
    ['Payment due date', order.payment_due_date || '—'],
    [totalsLabel, totalsValue],
  ];
}

async function exportBusinessDocPdf(order, config, options = {}) {
  const branding = resolveExportBranding(options);
  const poweredBy = brandingPoweredByLine(branding);
  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const generatedAt = new Date().toLocaleString('sv-SE', { hour12: false });
  const logoDataUrl = await loadBrandingLogoDataUrl(branding);
  const rows = config.rowsFactory(order);
  const totalValue = rows.reduce((sum, r) => sum + Number(r.lineTotal || 0), 0);

  drawPageBorder(doc, pageWidth, pageHeight);
  drawBusinessDocHeader(doc, order, config.title, generatedAt, branding);

  if (logoDataUrl) {
    addLogoImage(doc, logoDataUrl, pageWidth / 2 - 18, margin + 80, 36);
  }

  autoTable(doc, {
    startY: margin + 132,
    body: docMetaRows(
      order,
      config.docNumber(order),
      config.totalLabel,
      `TZS ${Math.round(totalValue).toLocaleString()}`
    ),
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 5, lineColor: [203, 213, 225], lineWidth: 0.5 },
    columnStyles: {
      0: { cellWidth: 130, fontStyle: 'bold', fillColor: [248, 250, 252], textColor: [15, 23, 42] },
      1: { cellWidth: pageWidth - margin * 2 - 130 },
    },
    margin: { left: margin, right: margin },
  });

  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || margin + 132) + 16,
    head: [ORDER_LINES_PDF_HEADERS],
    body: rows.map((r) => [
      r.product || '—',
      String(r.qty),
      formatExportPdfMoney(r.unitCost),
      formatExportPdfMoney(r.lineTotal),
    ]),
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 5, lineColor: [203, 213, 225], lineWidth: 0.5, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: ORDER_LINES_PDF_COLUMN_STYLES,
    margin: { left: margin, right: margin, bottom: 70 },
  });

  const footerY = pageHeight - 42;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(config.footer, margin, footerY);
  if (poweredBy) {
    doc.text(poweredBy, margin, footerY + 11);
  }

  doc.save(`${safeFilename(branding.businessName)}_${config.filename(order)}`);
}

export async function exportProformaPdf(order, options = {}) {
  if (!order) return;
  await exportBusinessDocPdf(order, {
    title: 'Proforma Invoice',
    docNumber: (o) => `PF-${String(o.po_number || `PO-${o.id}`).replace(/^PO-/, '')}`,
    totalLabel: 'Proforma total',
    rowsFactory: (o) => orderBaseDocRows(o, false).filter((r) => r.qty > 0),
    footer: 'Proforma invoice - prices and quantities subject to final confirmation.',
    filename: (o) => `${safeFilename(`proforma_${o.po_number || o.id}`)}.pdf`,
  }, options);
}

export async function exportInvoicePdf(order, options = {}) {
  if (!order) return;
  await exportBusinessDocPdf(order, {
    title: 'Tax Invoice',
    docNumber: (o) => `INV-${String(o.po_number || `PO-${o.id}`).replace(/^PO-/, '')}`,
    totalLabel: 'Invoice total',
    rowsFactory: (o) => orderBaseDocRows(o, true).filter((r) => r.qty > 0),
    footer: 'Invoice generated from received quantities in purchase workflow.',
    filename: (o) => `${safeFilename(`invoice_${o.po_number || o.id}`)}.pdf`,
  }, options);
}
